import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { MapPin, X, Loader2, Car, Footprints, Navigation, LayoutGrid, Tent, PartyPopper } from 'lucide-react';
import useOverflowFade from '../hooks/useOverflowFade';
import ProvincePillDropdown from './ProvincePillDropdown';
import { loadLeaflet } from '../lib/leafletLoader';

const MapExplore = ({ stalls, onStallClick, selectedMarket, searchQuery = '', fullHeight, directionsTarget, onClearDirections, expoNames = [], festivalNames = [], marketNames = [], provinceItems = {} }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const destMarkerRef = useRef(null);
  const userMarkerRef = useRef(null);

  const [routeInfo, setRouteInfo] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [travelMode, setTravelMode] = useState('walking'); // 'driving' or 'walking'
  const [directionsActive, setDirectionsActive] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [destinationCoords, setDestinationCoords] = useState(null);
  const [mapFilter, setMapFilter] = useState('all'); // 'all', 'markets', 'expos', 'festivals'
  const [mapSubFilter, setMapSubFilter] = useState('all'); // 'all' or specific market/expo/festival name
  const [mapProvince, setMapProvince] = useState('all'); // 'all' or one of the 9 SA provinces
  const mapSubFilterFade = useOverflowFade([mapFilter, marketNames, expoNames, festivalNames, mapProvince], { leftFade: false });

  // Dynamic map height — fills viewport minus its top offset. Handles PWA
  // standalone chrome, iOS safe-area, and dynamic filter row heights.
  const mapWrapperRef = useRef(null);
  const [dynamicHeight, setDynamicHeight] = useState(null);
  useEffect(() => {
    if (!fullHeight) return undefined;
    const recompute = () => {
      const el = mapWrapperRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // 16px breathing room at the bottom + iOS safe-area handled by parent
      const h = Math.max(320, window.innerHeight - top - 16);
      setDynamicHeight(h);
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('orientationchange', recompute);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recompute) : null;
    if (ro && document.body) ro.observe(document.body);
    // Recompute after fonts / images settle
    const t = setTimeout(recompute, 300);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('orientationchange', recompute);
      if (ro) ro.disconnect();
      clearTimeout(t);
    };
  }, [fullHeight]);

  // Province-filtered location lists
  const provinceMarkets = useMemo(() => {
    if (mapProvince === 'all') return marketNames;
    const allow = new Set(provinceItems?.[mapProvince]?.markets || []);
    return marketNames.filter((m) => allow.has(m));
  }, [marketNames, provinceItems, mapProvince]);
  const provinceExpos = useMemo(() => {
    if (mapProvince === 'all') return expoNames;
    const allow = new Set(provinceItems?.[mapProvince]?.expos || []);
    return expoNames.filter((e) => allow.has(e));
  }, [expoNames, provinceItems, mapProvince]);
  const provinceFestivals = useMemo(() => {
    if (mapProvince === 'all') return festivalNames;
    const allow = new Set(provinceItems?.[mapProvince]?.festivals || []);
    return festivalNames.filter((f) => allow.has(f));
  }, [festivalNames, provinceItems, mapProvince]);
  const provinceAllowedLocations = useMemo(() => {
    if (mapProvince === 'all') return null;
    const p = provinceItems?.[mapProvince] || {};
    return new Set([...(p.markets || []), ...(p.expos || []), ...(p.festivals || [])]);
  }, [provinceItems, mapProvince]);

  const stallsWithLocation = useMemo(() => {
    // Case/whitespace-insensitive sets for robust legacy fallback matching.
    // A production stall might have "kragdag" or " KragDag" while expos has "KragDag".
    const normalize = (v) => (v || '').toString().trim().toLowerCase();
    const expoSet = new Set(expoNames.map(normalize));
    const festivalSet = new Set(festivalNames.map(normalize));
    const query = searchQuery.toLowerCase().trim();
    const result = [];
    stalls.forEach(stall => {
      // Search filter — match same fields as Explore view
      if (query) {
        const searchable = [stall.stallName, stall.category, stall.description, ...(stall.tags || []), ...(stall.markets || [])].join(' ').toLowerCase();
        if (!searchable.includes(query)) return;
      }
      const locations = stall.locations || [];
      locations.forEach(loc => {
        if (loc.lat && loc.lng && loc.lat >= -90 && loc.lat <= 90 && loc.lng >= -180 && loc.lng <= 180) {
          if (selectedMarket && selectedMarket !== 'All Markets' && loc.market !== selectedMarket) return;
          // Determine the location's type. Prefer the explicit `type` field
          // (set on all new/migrated locations); fall back to name-based lookup
          // against the market/expo/festival collections for legacy records.
          const locNameNorm = normalize(loc.market);
          const locType = loc.type
            || (expoSet.has(locNameNorm) ? 'expo'
                : (festivalSet.has(locNameNorm) ? 'festival' : 'market'));
          // Filter by map type filter
          if (mapFilter !== 'all') {
            if (mapFilter === 'markets' && locType !== 'market') return;
            if (mapFilter === 'expos' && locType !== 'expo') return;
            if (mapFilter === 'festivals' && locType !== 'festival') return;
          }
          // Filter by sub-filter (specific market/expo/festival name)
          if (mapSubFilter !== 'all' && loc.market !== mapSubFilter) return;
          // Filter by province (location must belong to the chosen province)
          if (provinceAllowedLocations && !provinceAllowedLocations.has(loc.market)) return;
          result.push({
            ...stall,
            displayLat: loc.lat,
            displayLng: loc.lng,
            displayMarket: loc.market,
            displayStallNumber: loc.stallNumber,
            displayType: locType,
          });
        }
      });
    });
    return result;
  }, [stalls, selectedMarket, mapFilter, mapSubFilter, searchQuery, expoNames, festivalNames, provinceAllowedLocations]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || stalls.length === 0) return;

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    // Compute center from ALL stalls with valid GPS (not just filtered)
    const allLocs = [];
    stalls.forEach(stall => {
      (stall.locations || []).forEach(loc => {
        if (loc.lat && loc.lng && loc.lat >= -90 && loc.lat <= 90 && loc.lng >= -180 && loc.lng <= 180) {
          allLocs.push([loc.lat, loc.lng]);
        }
      });
    });
    const center = allLocs.length > 0
      ? [allLocs.reduce((a, b) => a + b[0], 0) / allLocs.length, allLocs.reduce((a, b) => a + b[1], 0) / allLocs.length]
      : [-25.75, 28.23];

    // Wait for Leaflet to load AND for the container to have dimensions
    // before creating the map. Leaflet is now lazy-loaded (removed from
    // index.html render-blocking chain) so we must await it here BEFORE
    // referencing `L` — previously an early `window.L` guard would bail
    // out before the loader had a chance to run.
    let cancelled = false;
    const tryInit = async () => {
      if (cancelled || !mapRef.current) return;
      if (mapInstanceRef.current) return; // Already initialized
      const h = mapRef.current.clientHeight;
      if (h < 50) {
        requestAnimationFrame(tryInit);
        return;
      }
      try { await loadLeaflet(); } catch (_) { return; }
      if (cancelled || !mapRef.current || mapInstanceRef.current) return;
      const L = window.L;
      if (!L) return;

      const map = L.map(mapRef.current, { zoomControl: false, attributionControl: false }).setView(center, 6);
      mapInstanceRef.current = map;

      // Add zoom control to bottom-left
      L.control.zoom({ position: 'bottomleft' }).addTo(map);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '',
        maxZoom: 19,
        minZoom: 3
      }).addTo(map);

      addStallMarkers();

      // If there's a pending directions request, start it now
      if (directionsTarget) {
        setTimeout(() => {
          startDirections(directionsTarget.lat, directionsTarget.lng, directionsTarget.stallName, directionsTarget.market);
        }, 200);
      }
    };
    requestAnimationFrame(tryInit);

    // ResizeObserver for container visibility changes
    const observer = new ResizeObserver(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    });
    observer.observe(mapRef.current);

    return () => {
      cancelled = true;
      observer.disconnect();
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [stalls]);

  // Refresh markers when filter changes
  useEffect(() => {
    if (mapInstanceRef.current && !directionsActive) {
      addStallMarkers();
      // Zoom to filtered stalls
      const L = window.L;
      if (L && stallsWithLocation.length > 0) {
        if (mapSubFilter !== 'all' || searchQuery.trim()) {
          // Zoom to fit filtered/searched stalls
          const bounds = L.latLngBounds(stallsWithLocation.map(s => [s.displayLat, s.displayLng]));
          if (bounds.isValid()) {
            try { mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 }); } catch(e) {}
          }
        } else if (mapFilter === 'all') {
          // Reset to full view
          const allLocs = [];
          stalls.forEach(stall => {
            (stall.locations || []).forEach(loc => {
              if (loc.lat && loc.lng && loc.lat >= -90 && loc.lat <= 90) allLocs.push([loc.lat, loc.lng]);
            });
          });
          if (allLocs.length > 0) {
            const center = [allLocs.reduce((a, b) => a + b[0], 0) / allLocs.length, allLocs.reduce((a, b) => a + b[1], 0) / allLocs.length];
            mapInstanceRef.current.setView(center, 6);
          }
        }
      }
    }
  }, [stallsWithLocation, directionsActive]);

  const addStallMarkers = useCallback(() => {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    // Clear existing markers layer
    if (markersLayerRef.current) {
      map.removeLayer(markersLayerRef.current);
    }

    const pinIcon = L.divIcon({
      html: `<div style="width:28px;height:28px;background:#FFD12D;border:3px solid #252525;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.3);pointer-events:none">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="#252525" style="pointer-events:none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
      </div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      popupAnchor: [0, -14],
      className: '',
    });

    const markersGroup = L.layerGroup();

    stallsWithLocation.forEach((stall) => {
      const marker = L.marker([stall.displayLat, stall.displayLng], { icon: pinIcon });
      
      const popupContent = document.createElement('div');
      popupContent.style.minWidth = '160px';
      popupContent.style.maxWidth = '200px';
      popupContent.innerHTML = `
        <p style="font-weight:700;font-size:13px;margin:0 0 2px;color:#252525">${stall.stallName}</p>
        <p style="font-size:11px;color:#666;margin:0 0 1px">${stall.displayMarket}</p>
        ${stall.displayStallNumber ? `<p style="font-size:10px;color:#999;margin:0 0 6px">Stall: ${stall.displayStallNumber}</p>` : '<div style="margin-bottom:6px"></div>'}
        <div style="display:flex;gap:4px">
          <button class="popup-view-btn" style="flex:1;font-size:11px;background:#FFD12D;color:#252525;font-weight:700;padding:6px 8px;border-radius:6px;border:none;cursor:pointer">View Stall</button>
          <button class="popup-dir-btn" style="flex:1;font-size:11px;background:#252525;color:white;font-weight:700;padding:6px 8px;border-radius:6px;border:none;cursor:pointer">Directions</button>
        </div>
      `;

      popupContent.querySelector('.popup-view-btn').addEventListener('click', () => {
        map.closePopup();
        onStallClick(stall);
      });
      popupContent.querySelector('.popup-dir-btn').addEventListener('click', () => {
        map.closePopup();
        startDirections(stall.displayLat, stall.displayLng, stall.stallName, stall.displayMarket);
      });

      marker.bindPopup(popupContent, { autoPanPaddingTopLeft: [10, 120], autoPanPaddingBottomRight: [10, 10] });
      markersGroup.addLayer(marker);
    });

    markersGroup.addTo(map);
    markersLayerRef.current = markersGroup;
  }, [stallsWithLocation, onStallClick]);

  // Handle external directions request
  useEffect(() => {
    if (directionsTarget && mapInstanceRef.current) {
      startDirections(directionsTarget.lat, directionsTarget.lng, directionsTarget.stallName, directionsTarget.market);
    }
  }, [directionsTarget]);

  // Re-fetch route when travel mode changes
  useEffect(() => {
    if (directionsActive && userLocation && destinationCoords) {
      fetchRoute(userLocation[0], userLocation[1], destinationCoords.lat, destinationCoords.lng, travelMode);
    }
  }, [travelMode]);

  const startDirections = (stallLat, stallLng, stallName, marketName) => {
    setDirectionsActive(true);
    setDestinationCoords({ lat: stallLat, lng: stallLng });
    setRouteInfo({ stallName, marketName, distance: null, duration: null });
    setLoadingRoute(true);
    setGpsError(null);

    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    // Hide all stall markers
    if (markersLayerRef.current) {
      map.removeLayer(markersLayerRef.current);
    }

    // Clear previous route
    clearRouteLayer();

    // Add destination marker (red pin)
    const destIcon = L.divIcon({
      html: `<div style="width:32px;height:32px;background:#E53935;border:3px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 10px rgba(0,0,0,0.4);pointer-events:none">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white" style="pointer-events:none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
      </div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      className: '',
    });
    destMarkerRef.current = L.marker([stallLat, stallLng], { icon: destIcon }).addTo(map)
      .bindPopup(`<strong>${stallName}</strong><br/><span style="font-size:11px">${marketName || ''}</span>`);

    // Request GPS
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);

          // "You are here" marker
          const userIcon = L.divIcon({
            html: `<div style="width:18px;height:18px;background:#4285F4;border:3px solid white;border-radius:50%;box-shadow:0 0 12px rgba(66,133,244,0.6);pointer-events:none"></div>`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            className: '',
          });
          userMarkerRef.current = L.marker([latitude, longitude], { icon: userIcon }).addTo(map)
            .bindPopup('<strong>You are here</strong>').openPopup();

          fetchRoute(latitude, longitude, stallLat, stallLng, travelMode);
        },
        (err) => {
          setLoadingRoute(false);
          if (err.code === 1) {
            // Permission denied
            setGpsError('Location access was denied. Please allow location access in your browser settings and try again.');
          } else if (err.code === 2) {
            setGpsError('Unable to determine your location. Please check your GPS settings.');
          } else {
            setGpsError('Location request timed out. Please try again.');
          }
          // Zoom to stall
          try { map.setView([stallLat, stallLng], 14); } catch(e) {}
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    } else {
      setLoadingRoute(false);
      setGpsError('Geolocation is not supported by your browser.');
    }
  };

  const fetchRoute = (userLat, userLng, stallLat, stallLng, mode) => {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    setLoadingRoute(true);
    clearRouteLayer();

    // OSRM demo server supports car/bike/foot profiles
    const osrmProfile = mode === 'walking' ? 'foot' : 'car';

    // Request multiple alternatives, then pick the SHORTEST by distance
    fetch(`https://router.project-osrm.org/route/v1/${osrmProfile}/${userLng},${userLat};${stallLng},${stallLat}?overview=full&geometries=geojson&alternatives=3`)
      .then(res => res.json())
      .then(data => {
        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
          // Pick the route with the smallest distance (shortest path)
          const shortest = data.routes.reduce((a, b) => (a.distance <= b.distance ? a : b));
          const coords = shortest.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
          const routeColor = mode === 'walking' ? '#4285F4' : '#252525';
          const routeLine = L.polyline(coords, { 
            color: routeColor, 
            weight: 5, 
            opacity: 0.8,
            dashArray: mode === 'walking' ? '10, 10' : null
          }).addTo(map);
          routeLayerRef.current = routeLine;

          const duration = Math.round(shortest.duration / 60);
          const distance = (shortest.distance / 1000).toFixed(1);
          setRouteInfo(prev => ({ ...prev, distance, duration, mode }));

          // Fit bounds to show full route
          try { map.fitBounds(L.latLngBounds([[userLat, userLng], [stallLat, stallLng]]).pad(0.3)); } catch(e) {}
        }
        setLoadingRoute(false);
      })
      .catch(() => setLoadingRoute(false));
  };

  const clearRouteLayer = () => {
    const map = mapInstanceRef.current;
    if (routeLayerRef.current && map) {
      map.removeLayer(routeLayerRef.current);
      routeLayerRef.current = null;
    }
  };

  const exitDirections = () => {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove route, destination and user markers
    clearRouteLayer();
    if (destMarkerRef.current) { map.removeLayer(destMarkerRef.current); destMarkerRef.current = null; }
    if (userMarkerRef.current) { map.removeLayer(userMarkerRef.current); userMarkerRef.current = null; }

    // Reset state
    setDirectionsActive(false);
    setRouteInfo(null);
    setGpsError(null);
    setDestinationCoords(null);
    if (onClearDirections) onClearDirections();

    // Restore stall markers
    addStallMarkers();

    // Reset view
    if (L && stallsWithLocation.length > 0) {
      const lats = stallsWithLocation.map(s => s.displayLat);
      const lngs = stallsWithLocation.map(s => s.displayLng);
      const center = [
        lats.reduce((a, b) => a + b, 0) / lats.length,
        lngs.reduce((a, b) => a + b, 0) / lngs.length,
      ];
      map.setView(center, 6);
    }
  };

  // Check if we should show "no stalls" popup on the map
  const showNoStallsMessage = stallsWithLocation.length === 0 && mapFilter !== 'all';

  return (
    <>
      {/* Filter Pills — rendered ABOVE the map (matches the Explore page pattern) */}
      {!directionsActive && (
        <div className="w-full flex flex-col items-center gap-2 mb-3">
          {/* Level 1: All / Markets / Expos / Festivals */}
          <div className="overflow-x-auto no-scrollbar max-w-full">
            <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 rounded-full p-1.5 w-max mx-auto">
              {[
                { key: 'all', label: 'All', icon: LayoutGrid },
                { key: 'markets', label: 'Markets', icon: MapPin },
                { key: 'expos', label: 'Expos', icon: Tent },
                { key: 'festivals', label: 'Festivals', icon: PartyPopper },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => { setMapFilter(key); setMapSubFilter('all'); setMapProvince('all'); }}
                  data-testid={`map-filter-${key}`}
                  className={`flex items-center justify-center gap-1 px-3 py-1.5 rounded-full font-bold text-xs transition-all duration-300 flex-shrink-0 ${
                    mapFilter === key
                      ? 'bg-[#FFD12D] text-[#252525]'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Level 2: Province dropdown + market/expo/festival names */}
          {mapFilter !== 'all' && (
            <div className="w-full flex justify-center">
              <div
                ref={mapSubFilterFade.ref}
                className="flex items-center gap-1.5 bg-white/90 dark:bg-[#252525]/90 backdrop-blur-sm rounded-full pl-0 pr-2 py-1.5 shadow-md overflow-x-auto no-scrollbar max-w-full"
                data-testid="map-sub-filter-pills"
                style={{
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-x',
                  overscrollBehaviorX: 'contain',
                  ...(mapSubFilterFade.maskStyle || {}),
                }}
              >
                <div className="sticky left-0 z-[5] flex-shrink-0 bg-white dark:bg-[#252525] -my-1.5 py-1.5 pl-3 pr-3 mr-1 rounded-l-full shadow-[6px_0_8px_-6px_rgba(0,0,0,0.15)]">
                  <ProvincePillDropdown
                    value={mapProvince}
                    onChange={(p) => { setMapProvince(p); setMapSubFilter('all'); }}
                    testId="map-province-dd"
                  />
                </div>
                {(mapFilter === 'markets' ? provinceMarkets : mapFilter === 'expos' ? provinceExpos : provinceFestivals).map(name => (
                  <button
                    key={name}
                    onClick={() => setMapSubFilter(name)}
                    className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
                      mapSubFilter === name
                        ? 'bg-[#FFD12D] text-[#252525]'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div ref={mapWrapperRef} className="relative z-0 w-full rounded-xl overflow-hidden border-2 border-[#FFD12D] shadow-lg mx-auto"
        style={{
          height: fullHeight ? (dynamicHeight ? `${dynamicHeight}px` : 'calc(100dvh - 260px)') : '500px',
          minHeight: fullHeight ? '320px' : '500px',
        }}
        data-testid="map-explore"
      >

      {/* Directions Panel */}
      {directionsActive && (
        <div className="absolute bottom-3 left-14 right-3 z-[1000]">
          {/* Info card */}
          <div className="bg-white dark:bg-[#252525] rounded-xl shadow-lg px-4 py-3">
            {/* Header row */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-[#252525] dark:text-white truncate">{routeInfo?.stallName}</p>
                {routeInfo?.marketName && <p className="text-xs text-gray-500 truncate">{routeInfo.marketName}</p>}
              </div>
              <button onClick={exitDirections} className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 ml-2">
                <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            {/* Walk / Drive toggle + Distance/ETA — all in one row */}
            <div className="flex items-center gap-2 mb-2 flex-nowrap">
              <button
                onClick={() => setTravelMode('walking')}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold transition-all flex-shrink-0 ${
                  travelMode === 'walking'
                    ? 'bg-[#4285F4] text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <Footprints className="w-3 h-3" />
                Walk
              </button>
              <button
                onClick={() => setTravelMode('driving')}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold transition-all flex-shrink-0 ${
                  travelMode === 'driving'
                    ? 'bg-[#252525] text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <Car className="w-3 h-3" />
                Drive
              </button>

              {/* Distance / Duration */}
              {loadingRoute ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#FFD12D] ml-auto flex-shrink-0" />
              ) : routeInfo?.distance ? (
                <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
                  <span className="text-xs font-bold bg-[#FFD12D] text-[#252525] px-2 py-1 rounded-full">{routeInfo.distance} km</span>
                  <span className="text-xs font-bold bg-gray-100 dark:bg-gray-700 text-[#252525] dark:text-white px-2 py-1 rounded-full">~{routeInfo.duration} min</span>
                </div>
              ) : null}
            </div>

            {/* GPS error message */}
            {gpsError && (
              <div className="flex items-center gap-2 text-xs bg-orange-50 dark:bg-orange-900/20 rounded-lg px-3 py-2">
                <Navigation className="w-3.5 h-3.5 flex-shrink-0 text-orange-600" />
                <span className="text-orange-600 flex-1">{gpsError}</span>
                <button
                  onClick={() => {
                    setGpsError(null);
                    if (destinationCoords) {
                      startDirections(destinationCoords.lat, destinationCoords.lng, routeInfo?.stallName, routeInfo?.marketName);
                    }
                  }}
                  className="flex-shrink-0 bg-[#FFD12D] text-[#252525] font-bold text-xs px-3 py-1 rounded-full"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div ref={mapRef} style={{ height: '100%', width: '100%' }} />

      {/* No stalls popup overlay */}
      {showNoStallsMessage && (
        <div className="absolute inset-0 z-[900] flex items-start justify-center pt-8 px-4 pointer-events-none">
          <div className="bg-white dark:bg-[#252525] rounded-xl shadow-lg px-6 py-4 text-center pointer-events-auto max-w-xs">
            <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="font-semibold text-sm text-[#252525] dark:text-white">No stalls found</p>
            <p className="text-xs text-gray-500 mt-1">No stalls with GPS data at this location yet.</p>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default MapExplore;
